// src/pages/training.tsx
'use client';

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loadRecentTraining } from "@/lib/training";
import { getSupabase, signOut } from "@/lib/supabase";
import { useModals } from "@/context/ModalContext";
import Sidebar from "@/components/Sidebar";
import {
  Home, Apple, Dumbbell, Trophy, Settings, LogOut,
  Calendar, Edit3, CalendarDays, Utensils,
  Activity, Zap, X
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, DotProps } from 'recharts';

// Custom dot with G/W badge
const SourceDot = (props: DotProps & { payload?: any }) => {
  const { cx, cy, payload } = props;
  if (!cx || !cy || !payload?.source) return null;

  const isWhoop = payload.source === "whoop";
  const color = isWhoop ? "#22c55e" : "#3b82f6";

  return (
    <g>
      <circle cx={cx} cy={cy} r={6} fill={color} stroke="#0f172a" strokeWidth={2} />
      <text x={cx} y={cy - 10} textAnchor="middle" fill="#0f172a" fontSize={11} fontWeight="bold">
        {isWhoop ? "W" : "G"}
      </text>
    </g>
  );
};

// Custom Tooltip with source attribution
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload;
    return (
      <div className="bg-[#f8fafc] border border-[#a78bfa] rounded-lg p-2 md:p-3 shadow-2xl text-xs md:text-sm">
        <p className="font-medium text-[#0f172a] mb-1">{label}</p>
        {payload.map((entry: any) => (
          <p key={entry.dataKey} style={{ color: entry.color }}>
            {entry.name}: <strong>{entry.value || '--'}</strong>
          </p>
        ))}
        {data.source && (
          <p className="text-[10px] md:text-xs mt-2 opacity-90">
            Data powered by{" "}
            <span className="font-bold text-green-400">
              {data.source === "whoop" ? "WHOOP" : "Garmin"}
            </span>
          </p>
        )}
      </div>
    );
  }
  return null;
};

// ——————————————————————————————————
// EDIT ZONES MODAL - Mobile responsive
// ——————————————————————————————————
function EditZonesModal({ profile, onClose, onSave }: { profile: any; onClose: () => void; onSave: (data: any) => void }) {
  const [form, setForm] = useState({
    zone_1_low_end: profile.zone_1_low_end || 0,
    zone_1_high_end: profile.zone_1_high_end || 0,
    zone_2_low_end: profile.zone_2_low_end || 0,
    zone_2_high_end: profile.zone_2_high_end || 0,
    zone_3_low_end: profile.zone_3_low_end || 0,
    zone_3_high_end: profile.zone_3_high_end || 0,
    zone_4_low_end: profile.zone_4_low_end || 0,
    zone_4_high_end: profile.zone_4_high_end || 0,
    zone_5_low_end: profile.zone_5_low_end || 0,
    zone_5_high_end: profile.zone_5_high_end || 0,
    "zone_1_carb_%": profile["zone_1_carb_%"] || 0,
    "zone_1_fat_%": profile["zone_1_fat_%"] || 0,
    "zone_2_carb_%": profile["zone_2_carb_%"] || 0,
    "zone_2_fat_%": profile["zone_2_fat_%"] || 0,
    "zone_3_carb_%": profile["zone_3_carb_%"] || 0,
    "zone_3_fat_%": profile["zone_3_fat_%"] || 0,
    "zone_4_carb_%": profile["zone_4_carb_%"] || 0,
    "zone_4_fat_%": profile["zone_4_fat_%"] || 0,
    "zone_5_carb_%": profile["zone_5_carb_%"] || 0,
    "zone_5_fat_%": profile["zone_5_fat_%"] || 0,
  });

  const handleSave = async () => {
    const { error } = await getSupabase()
      .from("client_training_profiles")
      .update(form)
      .eq("user_id", profile.user_id);
    if (!error) {
      onSave(form);
      onClose();
    } else {
      alert("Save failed");
      console.error(error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold">Edit HR & Fuel Zones</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        {[1,2,3,4,5].map(z => (
          <div key={z} className="mb-4 md:mb-6 p-3 md:p-5 bg-[#f1f5f9] rounded-xl">
            <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-green-400">Zone {z}</h3>
            <div className="grid grid-cols-2 gap-2 md:gap-4 text-sm">
              <div>
                <label className="text-[10px] md:text-xs text-gray-500 mb-1 block">Low HR</label>
                <input 
                  type="number" 
                  placeholder="Low HR" 
                  value={form[`zone_${z}_low_end` as keyof typeof form]} 
                  onChange={e => setForm(p => ({...p, [`zone_${z}_low_end`]: Number(e.target.value)}))} 
                  className="bg-[#ffffff] px-3 md:px-4 py-2 md:py-3 rounded-lg text-[#0f172a] w-full text-sm" 
                />
              </div>
              <div>
                <label className="text-[10px] md:text-xs text-gray-500 mb-1 block">High HR</label>
                <input 
                  type="number" 
                  placeholder="High HR" 
                  value={form[`zone_${z}_high_end` as keyof typeof form]} 
                  onChange={e => setForm(p => ({...p, [`zone_${z}_high_end`]: Number(e.target.value)}))} 
                  className="bg-[#ffffff] px-3 md:px-4 py-2 md:py-3 rounded-lg text-[#0f172a] w-full text-sm" 
                />
              </div>
              <div>
                <label className="text-[10px] md:text-xs text-gray-500 mb-1 block">Carb %</label>
                <input 
                  type="number" 
                  placeholder="Carb %" 
                  value={form[`zone_${z}_carb_%` as keyof typeof form]} 
                  onChange={e => setForm(p => ({...p, [`zone_${z}_carb_%`]: Number(e.target.value)}))} 
                  className="bg-[#ffffff] px-3 md:px-4 py-2 md:py-3 rounded-lg text-[#0f172a] w-full text-sm" 
                />
              </div>
              <div>
                <label className="text-[10px] md:text-xs text-gray-500 mb-1 block">Fat %</label>
                <input 
                  type="number" 
                  placeholder="Fat %" 
                  value={form[`zone_${z}_fat_%` as keyof typeof form]} 
                  onChange={e => setForm(p => ({...p, [`zone_${z}_fat_%`]: Number(e.target.value)}))} 
                  className="bg-[#ffffff] px-3 md:px-4 py-2 md:py-3 rounded-lg text-[#0f172a] w-full text-sm" 
                />
              </div>
            </div>
          </div>
        ))}
        <div className="flex gap-2 md:gap-4 mt-4 md:mt-8">
          <button onClick={handleSave} className="flex-1 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold py-3 md:py-4 rounded-xl text-sm md:text-lg">Save Changes</button>
          <button onClick={onClose} className="flex-1 bg-gray-700 hover:bg-gray-600 text-[#0f172a] py-3 md:py-4 rounded-xl text-sm md:text-lg">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ——————————————————————————————————
// MAIN TRAINING PAGE — MOBILE RESPONSIVE
// ——————————————————————————————————
export default function Training() {
  const { openAddActivity, openLogFood, openUpdateGoal } = useModals();

  const [trainingData, setTrainingData] = useState<any[]>([]);
  const [calorieData, setCalorieData] = useState<any[]>([]);
  const [hrData, setHrData] = useState<any[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activityBreakdown, setActivityBreakdown] = useState<any[]>([]);

  const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  const loadProfile = async () => {
    const { data: { user } } = await getSupabase().auth.getUser();
    if (!user?.id) return;
    const { data } = await getSupabase()
      .from("client_training_profiles")
      .select(`
        id,
        user_id,
        zone_1_low_end, zone_1_high_end,
        zone_2_low_end, zone_2_high_end,
        zone_3_low_end, zone_3_high_end,
        zone_4_low_end, zone_4_high_end,
        zone_5_low_end, zone_5_high_end,
        "zone_1_carb_%", "zone_1_fat_%",
        "zone_2_carb_%", "zone_2_fat_%",
        "zone_3_carb_%", "zone_3_fat_%",
        "zone_4_carb_%", "zone_4_fat_%",
        "zone_5_carb_%", "zone_5_fat_%"
      `)
      .eq("user_id", user.id)
      .single();
    setProfile(data || {
      zone_1_low_end: 80, zone_1_high_end: 100,
      zone_2_low_end: 100, zone_2_high_end: 120,
      zone_3_low_end: 120, zone_3_high_end: 140,
      zone_4_low_end: 140, zone_4_high_end: 160,
      zone_5_low_end: 160, zone_5_high_end: 180,
      "zone_1_carb_%": 48, "zone_1_fat_%": 52,
      "zone_2_carb_%": 44, "zone_2_fat_%": 56,
      "zone_3_carb_%": 76, "zone_3_fat_%": 24,
      "zone_4_carb_%": 92, "zone_4_fat_%": 8,
      "zone_5_carb_%": 98, "zone_5_fat_%": 2,
    });
  };

  const refreshTraining = async () => {
    const logs = await loadRecentTraining(50);
    setTrainingData(logs || []);

    // Activity breakdown - cap at 10, combine rest into "Other"
    const activityCounts: Record<string, number> = {};
    logs?.forEach(log => {
      const type = log.activity_type || 'Unknown';
      activityCounts[type] = (activityCounts[type] || 0) + 1;
    });
    
    // Sort by count descending
    const sorted = Object.entries(activityCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    
    // If more than 10 activity types, cap at 9 + "Other"
    let breakdown;
    if (sorted.length > 10) {
      const top9 = sorted.slice(0, 9);
      const otherCount = sorted.slice(9).reduce((sum, item) => sum + item.value, 0);
      breakdown = [...top9, { name: 'Other', value: otherCount }];
    } else {
      breakdown = sorted;
    }
    
    setActivityBreakdown(breakdown);

    const dailyCal: Record<string, { cal: number; source?: string }> = {};
    const dailyHr: Record<string, { avg: number; max: number; source?: string }> = {};

    logs?.forEach(log => {
      const day = new Date(log.created_at).toLocaleDateString('en-US', { weekday: 'short' });

      const cals = Number(log.calories_burned) || 0;
      if (cals > 0) {
        if (!dailyCal[day] || cals > dailyCal[day].cal) {
          dailyCal[day] = { cal: cals, source: log.source };
        }
      }

      const avg = Number(log.avg_heart_rate) || 0;
      const max = Number(log.max_heart_rate) || 0;
      if (avg > 0 || max > 0) {
        if (!dailyHr[day]) dailyHr[day] = { avg: 0, max: 0, source: log.source };
        if (avg > dailyHr[day].avg) dailyHr[day].avg = avg;
        if (max > dailyHr[day].max) dailyHr[day].max = max;
      }
    });

    const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    setCalorieData(order.map(d => ({
      day: d,
      cal: dailyCal[d]?.cal || null,
      source: dailyCal[d]?.source
    })));

    setHrData(order.map(d => ({
      day: d,
      avgHr: dailyHr[d]?.avg || null,
      maxHr: dailyHr[d]?.max || null,
      source: dailyHr[d]?.source
    })));

    await loadProfile();
  };

  useEffect(() => { refreshTraining(); }, []);

  const avgCalories = Math.round(calorieData.reduce((s, d) => s + (d.cal || 0), 0) / 7) || 0;

  // Zone colors
  const zoneColors = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="flex h-screen bg-[#ffffff] text-[#0f172a] overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
        {/* Header - stacks on mobile */}
        <header className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Training</h1>
            <p className="text-gray-500 mt-1 text-sm md:text-base">Your performance, recovery, and progress</p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 w-full sm:w-auto">
            <button
              onClick={openAddActivity}
              className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-green-500 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-green-400 transition whitespace-nowrap"
            >
              + Activity
            </button>
            <button
              onClick={openLogFood}
              className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-green-500 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-green-400 transition whitespace-nowrap"
            >
              + Food
            </button>
            <button
              onClick={openUpdateGoal}
              className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-gray-700 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-gray-600 transition whitespace-nowrap"
            >
              Update Goal
            </button>
          </div>
        </header>

        <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500 mb-4 md:mb-6">
          <Calendar className="w-4 h-4" />
          <span>Current Week</span>
        </div>

        {/* TOP CARDS - Responsive grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6 mb-4 md:mb-6">
          {/* HR Zones with Visual Bars */}
          <div className="md:col-span-1 lg:col-span-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
            <div className="flex justify-between items-center mb-3 md:mb-4">
              <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
                <Activity className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                HR Zones
              </h2>
              <Edit3 className="w-4 h-4 text-gray-500 cursor-pointer hover:text-[#0f172a]" onClick={() => setShowEditModal(true)} />
            </div>
            <div className="space-y-2 md:space-y-3">
              {[1,2,3,4,5].map(z => {
                const low = profile?.[`zone_${z}_low_end`] || 0;
                const high = profile?.[`zone_${z}_high_end`] || 0;
                const color = zoneColors[z - 1];
                return (
                  <div key={z} className="group">
                    <div className="flex justify-between text-xs md:text-sm mb-1">
                      <span className="text-gray-500">Zone {z}</span>
                      <span className="font-medium">{low} – {high} bpm</span>
                    </div>
                    <div className="h-1.5 md:h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full transition-all duration-300 group-hover:opacity-80"
                        style={{ 
                          width: `${(z / 5) * 100}%`,
                          backgroundColor: color
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Activity Breakdown */}
          <div className="md:col-span-1 lg:col-span-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
            <div className="flex justify-between items-center mb-3 md:mb-4">
              <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                Activity Types
              </h2>
            </div>
            {activityBreakdown.length > 0 ? (
              <div className="space-y-2 md:space-y-3">
                {activityBreakdown.map((activity, idx) => {
                  const total = activityBreakdown.reduce((sum, a) => sum + a.value, 0);
                  const percentage = Math.round((activity.value / total) * 100);
                  return (
                    <div key={activity.name}>
                      <div className="flex justify-between text-xs md:text-sm mb-1">
                        <span className="text-gray-500 capitalize truncate mr-2">{activity.name}</span>
                        <span className="font-medium flex-shrink-0">{percentage}%</span>
                      </div>
                      <div className="h-1.5 md:h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full transition-all duration-300"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: COLORS[idx % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-6 md:py-8">No activity data</p>
            )}
          </div>

          {/* Goal + VO₂ Max - Stack on mobile, side by side on tablet+ */}
          <div className="md:col-span-2 lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-3 md:gap-4">
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
              <div className="flex justify-between items-center mb-2 md:mb-4">
                <h2 className="text-base md:text-lg font-semibold">Goal</h2>
                <Edit3 className="w-4 h-4 text-gray-500 cursor-pointer" />
              </div>
              <div className="space-y-1.5 md:space-y-3">
                <div className="flex justify-between text-xs md:text-sm"><span className="text-gray-500">Type</span><span className="font-medium">Gain muscle</span></div>
                <div className="flex justify-between text-xs md:text-sm"><span className="text-gray-500">Target</span><span className="font-medium">90kg</span></div>
                <div className="flex justify-between text-xs md:text-sm"><span className="text-gray-500">Date</span><span className="font-medium">12 Jul 2026</span></div>
              </div>
            </div>
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
              <div className="flex justify-between items-center mb-2 md:mb-4">
                <h2 className="text-base md:text-lg font-semibold">VO₂ Max</h2>
                <Edit3 className="w-4 h-4 text-gray-500 cursor-pointer" />
              </div>
              <p className="text-2xl md:text-3xl font-bold text-green-400">145</p>
              <p className="text-[10px] md:text-xs text-gray-500 mt-1">Elite level</p>
            </div>
          </div>
        </div>

        {/* CHARTS WITH SOURCE ATTRIBUTION - Stack on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          {/* Heart Rate */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
            <div className="flex justify-between items-center mb-3 md:mb-4">
              <h2 className="text-base md:text-lg font-semibold">Heart rate</h2>
              <span className="text-xs md:text-sm text-gray-500">Avg + Peak</span>
            </div>
            <div className="h-[180px] md:h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hrData}>
                  <CartesianGrid stroke="#475569" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: '#545f6e', fontSize: 10 }} />
                  <YAxis domain={[80, 'dataMax + 20']} tick={{ fill: '#545f6e', fontSize: 10 }} width={35} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '10px' }}
                    payload={[
                      { value: 'Avg HR', type: 'line', color: '#a78bfa' },
                      { value: 'Max HR', type: 'line', color: '#f87171' }
                    ]} 
                  />
                  <Line type="monotone" dataKey="avgHr" stroke="#a78bfa" strokeWidth={2} dot={<SourceDot />} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="maxHr" stroke="#f87171" strokeWidth={2} dot={<SourceDot />} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 md:gap-6 mt-2 md:mt-3 text-[10px] md:text-xs text-gray-500">
              <span className="flex items-center gap-1.5 md:gap-2"><div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500"></div> WHOOP</span>
              <span className="flex items-center gap-1.5 md:gap-2"><div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500"></div> Garmin</span>
            </div>
          </div>

          {/* Calories Burned */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
            <div className="flex justify-between items-center mb-3 md:mb-4">
              <h2 className="text-base md:text-lg font-semibold">Calories burned</h2>
              <span className="text-xs md:text-sm text-gray-500">{avgCalories}kcal avg.</span>
            </div>
            <div className="h-[180px] md:h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={calorieData}>
                  <CartesianGrid stroke="#475569" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: '#545f6e', fontSize: 10 }} />
                  <YAxis domain={[0, 'dataMax + 500']} tick={{ fill: '#545f6e', fontSize: 10 }} width={40} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="cal" stroke="#a78bfa" strokeWidth={2} dot={<SourceDot />} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 md:gap-6 mt-2 md:mt-3 text-[10px] md:text-xs text-gray-500">
              <span className="flex items-center gap-1.5 md:gap-2"><div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500"></div> WHOOP</span>
              <span className="flex items-center gap-1.5 md:gap-2"><div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500"></div> Garmin</span>
            </div>
          </div>
        </div>

        {/* TRAINING SUMMARY */}
        <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
          <div className="flex justify-between items-center mb-3 md:mb-4">
            <h2 className="text-base md:text-lg font-semibold">Training Summary</h2>
            <Link 
              to="/training"
              className="text-xs text-green-400 hover:text-green-300 transition font-medium flex items-center gap-1"
            >
              View All
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {/* Weekly Stats */}
          {trainingData.length > 0 && (
            <div className="grid grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4 pb-3 md:pb-4 border-b border-[#e2e8f0]">
              <div className="text-center">
                <p className="text-lg md:text-2xl font-bold text-green-400">
                  {trainingData.length}
                </p>
                <p className="text-[10px] md:text-xs text-gray-500">Workouts</p>
              </div>
              <div className="text-center">
                <p className="text-lg md:text-2xl font-bold text-green-400">
                  {trainingData.reduce((sum, t) => sum + (Number(t.calories_burned) || 0), 0).toLocaleString()}
                </p>
                <p className="text-[10px] md:text-xs text-gray-500">Total Cals</p>
              </div>
              <div className="text-center">
                <p className="text-lg md:text-2xl font-bold text-green-400">
                  {Math.round(
                    trainingData.reduce((sum, t) => sum + (Number(t.avg_heart_rate) || 0), 0) / trainingData.length
                  ) || 0}
                </p>
                <p className="text-[10px] md:text-xs text-gray-500">Avg HR</p>
              </div>
            </div>
          )}

          {/* Training List */}
          <div className="space-y-2 md:space-y-3 max-h-64 md:max-h-96 overflow-y-auto pr-2 custom-scrollbar">
            {trainingData.length === 0 ? (
              <div className="text-center py-6 md:py-8">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 rounded-full bg-gray-800/50 flex items-center justify-center">
                  <Dumbbell className="w-6 h-6 md:w-8 md:h-8 text-gray-600" />
                </div>
                <p className="text-gray-500 text-sm mb-2">No recent training sessions</p>
                <button
                  onClick={openAddActivity}
                  className="text-xs text-green-400 hover:text-green-300 transition font-medium"
                >
                  Log your first workout →
                </button>
              </div>
            ) : (
              trainingData.map((t, i) => (
                <div 
                  key={t.id || i} 
                  className="flex items-center justify-between text-sm p-2 md:p-3 rounded-xl hover:bg-[#f1f5f9] transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className="w-8 h-8 md:w-9 md:h-9 rounded flex items-center justify-center overflow-hidden bg-black/50 group-hover:scale-110 transition-transform flex-shrink-0">
                      {t.source === "garmin" ? (
                        <img src="https://static.garmincdn.com/com.garmin.connect/content/images/developer/gc-app-tile/xxxhdpi/gc-app-tile_@480.png" alt="Garmin" className="w-full h-full object-contain" />
                      ) : t.source === "whoop" ? (
                        <img src="https://usbamprqshmoalvlkjch.supabase.co/storage/v1/object/public/branding/WHOOPWHITELOGO2.png" alt="WHOOP" className="w-full h-full object-contain px-1" />
                      ) : (
                        <div className="w-full h-full rounded bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xs font-bold text-[#0f172a]">
                          {t.activity_type?.[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-600 capitalize group-hover:text-[#0f172a] transition-colors text-xs md:text-sm truncate">
                        {t.activity_type || "Unknown"}
                      </p>
                      <p className="text-[10px] md:text-xs text-gray-500">
                        {new Date(t.created_at).toLocaleDateString("en-GB", { 
                          day: "numeric", 
                          month: "short",
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="text-green-400 font-medium text-[10px] md:text-xs">
                      {t.avg_heart_rate ? `${t.avg_heart_rate} bpm` : "--"}
                    </p>
                    <p className="text-[10px] md:text-xs text-gray-500">
                      {t.calories_burned ? `${t.calories_burned} Cal` : "--"}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Custom Scrollbar Styles */}
        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #a78bfa;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #8b5cf6;
          }
        `}</style>

        <footer className="mt-8 md:mt-12 text-center text-gray-500 text-xs">
          © {new Date().getFullYear()} Input
        </footer>

        {showEditModal && profile && (
          <EditZonesModal profile={profile} onClose={() => setShowEditModal(false)} onSave={(updated) => setProfile((p: any) => ({ ...p, ...updated }))} />
        )}
      </main>
    </div>
  );
}
